import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw,
  Download,
  Send,
  Search,
  Loader2,
  Mail,
  X,
} from "lucide-react";
import AdminShell from "../components/AdminShell";
import {
  fetchWaitlist,
  waitlistCsvUrl,
  reactivateWaitlistSignup,
} from "../lib/api";

/**
 * AdminWaitlist — `/admin/waitlist`.
 *
 * Lists every mobile-app waitlist signup with their optional "Why I
 * signed up" reason, a CSV export button, and a per-row "Reactivate"
 * action that composes a warm email via Resend. The admin gate lives
 * on the backend (DEV_EMAILS) — non-admins see a 403 landing.
 */

const PLATFORM_LABELS = {
  ios: "iOS",
  android: "Android",
  both: "iOS + Android",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const SummaryStat = ({ label, value, testid }) => (
  <div
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-4"
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
      {label}
    </div>
    <div className="text-3xl font-light text-white tabular-nums">{value}</div>
  </div>
);

const PlatformPill = ({ platform }) => {
  const label = PLATFORM_LABELS[platform] || platform;
  const tone = {
    ios: "bg-[#3F6FE8]/15 text-[#93B2F2] border-[#3F6FE8]/30",
    android: "bg-[#3DA86B]/15 text-[#7BD49D] border-[#3DA86B]/30",
    both: "bg-[#D4B483]/15 text-[#D4B483] border-[#D4B483]/30",
  }[platform] || "bg-white/10 text-white/70 border-white/15";
  return (
    <span
      className={`text-[10px] uppercase tracking-[0.18em] rounded-full px-2.5 py-1 border ${tone}`}
    >
      {label}
    </span>
  );
};

const ReactivateDialog = ({ signup, onClose, onSent }) => {
  const [note, setNote] = useState(
    "You told us what you wanted. We built it — come try the web app and tell us what you think.",
  );
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      const res = await reactivateWaitlistSignup(signup.id, {
        note: note.trim() || undefined,
        subject: subject.trim() || undefined,
      });
      toast.success(`Sent to ${res.sent_to}`);
      onSent?.(signup.id);
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 502) {
        toast.error("Resend rejected the email — check the address + keys.");
      } else {
        toast.error("Couldn't send the reactivation email.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-waitlist-reactivate-modal"
    >
      <div className="w-full max-w-lg bg-[#0F1115] border border-white/10 rounded-2xl p-6 animate-in fade-in slide-in-from-bottom-4 duration-200">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1">
              Reactivation email
            </div>
            <h2 className="text-lg text-white font-medium leading-snug">
              Email {signup.name || "(no name)"}
            </h2>
            <p className="text-xs text-white/50 mt-0.5">{signup.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white transition-colors"
            aria-label="Close"
            data-testid="admin-waitlist-reactivate-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {signup.reason && (
          <div className="bg-[#D4B483]/10 border-l-2 border-[#D4B483] rounded-r-md px-3 py-2 mb-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4B483] mb-0.5">
              They told us
            </div>
            <div className="text-sm text-white/85 leading-snug whitespace-pre-wrap">
              {signup.reason}
            </div>
          </div>
        )}

        <label className="block text-[10px] uppercase tracking-[0.22em] text-white/50 mb-1.5">
          Subject (optional)
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          placeholder="A quick nudge from TruBeauté ✨"
          className="w-full bg-white/[0.04] border border-white/10 focus:border-[#D4B483]/50 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 mb-4 focus:outline-none transition-colors"
          data-testid="admin-waitlist-reactivate-subject"
        />

        <label className="block text-[10px] uppercase tracking-[0.22em] text-white/50 mb-1.5">
          Note · shown as highlighted block in the email
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1500}
          rows={4}
          className="w-full bg-white/[0.04] border border-white/10 focus:border-[#D4B483]/50 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 mb-1 focus:outline-none resize-none transition-colors"
          data-testid="admin-waitlist-reactivate-note"
        />
        <div className="text-[10px] text-white/40 mb-5 text-right tabular-nums">
          {note.length} / 1500
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="bg-white/[0.06] hover:bg-white/[0.12] text-white/80 rounded-full px-4 py-2 text-sm transition-colors"
            data-testid="admin-waitlist-reactivate-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={sending}
            className="inline-flex items-center gap-2 bg-[#D4B483] hover:bg-[#c8a56d] text-[#1a1416] rounded-full px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-wait"
            data-testid="admin-waitlist-reactivate-send"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Send email
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminWaitlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("all");
  const [reactivating, setReactivating] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWaitlist();
      setItems(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError("unauthorized");
      else if (status === 403) setError("forbidden");
      else setError("network");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((row) => {
      if (platform !== "all" && row.platform !== platform) return false;
      if (!needle) return true;
      const hay = [
        row.email || "",
        row.name || "",
        row.reason || "",
      ].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [items, q, platform]);

  const stats = useMemo(() => {
    const byPlatform = { ios: 0, android: 0, both: 0 };
    let withReason = 0;
    items.forEach((r) => {
      byPlatform[r.platform] = (byPlatform[r.platform] || 0) + 1;
      if (r.reason && r.reason.trim()) withReason += 1;
    });
    return { byPlatform, withReason };
  }, [items]);

  return (
    <AdminShell
      eyebrow="Waitlist"
      title="Mobile-app waitlist"
      subtitle="Every signup for the iOS + Android launch, freshest on top. Click a row's Email button to compose a personalised reactivation note — their original ‘why I signed up’ text is inlined so you can reference it directly."
      actions={
        <div className="flex items-center gap-2">
          <a
            href={waitlistCsvUrl}
            className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white rounded-full px-4 py-2 transition-all text-sm"
            data-testid="admin-waitlist-csv-download"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </a>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white rounded-full px-4 py-2 transition-all text-sm"
            data-testid="admin-waitlist-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      }
    >
      <div data-testid="admin-waitlist">
        {loading && (
          <div
            className="text-sm text-white/60"
            data-testid="admin-waitlist-loading"
          >
            Reading the waitlist…
          </div>
        )}

        {error === "forbidden" && (
          <div
            className="bg-[#7F2A2A]/20 border border-[#B85F5F]/40 text-[#F4B8C0] rounded-2xl p-5 text-sm"
            data-testid="admin-waitlist-forbidden"
          >
            This view is for admins only.
          </div>
        )}

        {error === "network" && (
          <div
            className="bg-[#7F2A2A]/20 border border-[#B85F5F]/40 text-[#F4B8C0] rounded-2xl p-5 text-sm"
            data-testid="admin-waitlist-network-error"
          >
            Couldn't load the waitlist. Please refresh.
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <SummaryStat
                label="Total"
                value={items.length}
                testid="admin-waitlist-total-stat"
              />
              <SummaryStat
                label="With reason"
                value={stats.withReason}
                testid="admin-waitlist-with-reason-stat"
              />
              <SummaryStat
                label="iOS + both"
                value={stats.byPlatform.ios + stats.byPlatform.both}
              />
              <SummaryStat
                label="Android + both"
                value={stats.byPlatform.android + stats.byPlatform.both}
              />
            </div>

            {/* Search + platform filter */}
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="h-4 w-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search email, name, or reason…"
                  className="w-full bg-white/[0.04] border border-white/10 focus:border-[#D4B483]/40 text-white placeholder-white/30 rounded-full pl-10 pr-4 py-2.5 text-sm transition-colors focus:outline-none"
                  data-testid="admin-waitlist-search"
                />
              </div>
              <div className="flex items-center gap-1 bg-white/[0.04] border border-white/10 rounded-full p-1">
                {[
                  { v: "all", label: "All" },
                  { v: "ios", label: "iOS" },
                  { v: "android", label: "Android" },
                  { v: "both", label: "Both" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setPlatform(opt.v)}
                    className={`rounded-full px-3 py-1 text-xs transition-colors ${
                      platform === opt.v
                        ? "bg-[#D4B483]/20 text-[#D4B483]"
                        : "text-white/60 hover:text-white"
                    }`}
                    data-testid={`admin-waitlist-filter-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div
                className="bg-white/[0.04] border border-white/10 rounded-2xl p-10 text-center"
                data-testid="admin-waitlist-empty"
              >
                <p className="text-white/60">
                  {items.length === 0
                    ? "No signups yet. Share the landing page — when someone joins the waitlist, they'll land here."
                    : "No signups match this filter."}
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-waitlist-list">
                {filtered.map((row) => (
                  <div
                    key={row.id}
                    className="bg-white/[0.04] border border-white/10 rounded-2xl p-5"
                    data-testid={`admin-waitlist-row-${row.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                      <div className="flex items-center gap-3 flex-wrap min-w-0">
                        <span className="text-white font-medium truncate max-w-xs">
                          {row.name || "(no name)"}
                        </span>
                        <PlatformPill platform={row.platform} />
                        <span className="text-xs text-white/50">
                          {formatDate(row.created_at)}
                        </span>
                      </div>
                      <button
                        onClick={() => setReactivating(row)}
                        className="inline-flex items-center gap-1.5 text-[#D4B483] hover:bg-[#D4B483]/10 border border-[#D4B483]/30 rounded-full px-3 py-1 text-xs transition-colors"
                        data-testid={`admin-waitlist-reactivate-${row.id}`}
                      >
                        <Mail className="h-3 w-3" /> Reactivate
                      </button>
                    </div>

                    <div
                      className="text-sm text-white/75 font-mono tracking-tight break-all mb-2"
                      data-testid={`admin-waitlist-email-${row.id}`}
                    >
                      {row.email}
                    </div>

                    {row.reason && (
                      <div
                        className="bg-[#D4B483]/10 border-l-2 border-[#D4B483] rounded-r-md px-3 py-2 text-sm text-white/85 leading-snug whitespace-pre-wrap"
                        data-testid={`admin-waitlist-reason-${row.id}`}
                      >
                        {row.reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {reactivating && (
        <ReactivateDialog
          signup={reactivating}
          onClose={() => setReactivating(null)}
          onSent={() => load()}
        />
      )}
    </AdminShell>
  );
}
